import { Form, Formik, useFormikContext } from "formik";
import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as Yup from "yup";

import {
  BusinessOutlined,
  CheckCircleOutlined,
  EmailOutlined,
  ForumOutlined,
  LockOutlined,
  PersonOutlined,
  PhoneOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const UserSchema = Yup.object().shape({
  organizationName: Yup.string()
    .min(2, "Too short")
    .max(100, "Too long")
    .required("Required"),
  name: Yup.string()
    .min(2, "Too short")
    .max(50, "Too long")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  phone: Yup.string()
    .matches(/^\+?[0-9\s\-().]{7,20}$/, "Invalid phone number")
    .required("Required"),
  password: Yup.string()
    .min(5, "Minimum 5 characters")
    .max(50, "Too long")
    .required("Required"),
});

const PERKS = [
  "Unlimited WhatsApp connections",
  "Real-time multi-agent inbox",
  "Ticket queues & routing rules",
  "Analytics dashboard",
];

function passwordStrength(password) {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 6) score += 25;
  if (password.length >= 10) score += 25;
  if (/[A-Z]/.test(password)) score += 25;
  if (/[0-9!@#$%^&*]/.test(password)) score += 25;
  return score;
}

function strengthLabel(score) {
  if (score <= 25) return { label: "Weak", color: "error" };
  if (score <= 50) return { label: "Fair", color: "warning" };
  if (score <= 75) return { label: "Good", color: "info" };
  return { label: "Strong", color: "success" };
}

const SignUpFields = ({
  showPassword,
  setShowPassword,
  passwordValue,
  setPasswordValue,
  strength,
  strengthText,
  strengthColor,
}) => {
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormikContext();

  return (
    <Form>
      <Stack spacing={2.5}>
        <TextField
          name="organizationName"
          label="Organization / Company name"
          fullWidth
          required
          autoFocus
          autoComplete="organization"
          value={values.organizationName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.organizationName && Boolean(errors.organizationName)}
          helperText={touched.organizationName && errors.organizationName}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessOutlined
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          name="name"
          label={i18n.t("signup.form.name")}
          fullWidth
          required
          autoComplete="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlined
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          name="email"
          label={i18n.t("signup.form.email")}
          type="email"
          fullWidth
          required
          autoComplete="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          name="phone"
          label="Phone number"
          type="tel"
          fullWidth
          required
          autoComplete="tel"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.phone && Boolean(errors.phone)}
          helperText={touched.phone && errors.phone}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneOutlined fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box>
          <TextField
            name="password"
            label={i18n.t("signup.form.password")}
            type={showPassword ? "text" : "password"}
            fullWidth
            required
            autoComplete="new-password"
            value={values.password}
            onChange={(e) => {
              setFieldValue("password", e.target.value);
              setPasswordValue(e.target.value);
            }}
            onBlur={handleBlur}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      fontSize="small"
                      sx={{ color: "text.disabled" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setShowPassword((p) => !p)}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {passwordValue.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={strength}
                color={strengthColor}
                sx={{ borderRadius: 1, height: 4 }}
              />
              <Typography
                variant="caption"
                color={`${strengthColor}.main`}
                sx={{ mt: 0.5, display: "block" }}
              >
                {strengthText} password
              </Typography>
            </Box>
          )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting}
          sx={{
            py: 1.4,
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {i18n.t("signup.buttons.submit")}
        </Button>
      </Stack>
    </Form>
  );
};

const SignUp = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");

  const handleSignUp = async (values) => {
    try {
      await api.post("/organizations/register", values);
      toast.success(i18n.t("signup.toasts.success"));
      navigate("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const strength = passwordStrength(passwordValue);
  const { label: strengthText, color: strengthColor } = strengthLabel(strength);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Brand panel (desktop only) ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "46%",
          flexShrink: 0,
          bgcolor: "primary.main",
          color: "#fff",
          p: { md: 5, lg: 7 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 480,
            height: 480,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.05)",
            top: -140,
            right: -140,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 320,
            height: 320,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.04)",
            bottom: -80,
            left: -80,
            pointerEvents: "none",
          }}
        />

        {/* Logo + headline */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.15)",
              mb: 2.5,
            }}
          >
            <ForumOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Typography
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.5px"
            lineHeight={1.2}
          >
            Hashlix CRM
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 1.5, opacity: 0.75, maxWidth: 300, lineHeight: 1.65 }}
          >
            Everything your team needs to turn WhatsApp into a support
            powerhouse.
          </Typography>
        </Box>

        {/* Perks list */}
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{ opacity: 0.55, letterSpacing: 1.5 }}
          >
            What you get
          </Typography>
          {PERKS.map((perk) => (
            <Box
              key={perk}
              sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <CheckCircleOutlined
                sx={{ fontSize: 20, opacity: 0.85, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{ opacity: 0.9, fontWeight: 500 }}
              >
                {perk}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Bottom: social proof */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            bgcolor: "rgba(255,255,255,0.10)",
            borderRadius: 2.5,
            p: 2.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", opacity: 0.85, lineHeight: 1.6 }}
          >
            "Hashlix cut our first-response time by 60%. Our agents love it."
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.55, mt: 1, display: "block" }}
          >
            — Head of Support, Logistics Co.
          </Typography>
        </Box>
      </Box>

      {/* ── Form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.default",
          p: { xs: 3, sm: 5, md: 6 },
        }}
      >
        {/* Mobile brand mark */}
        <Box sx={{ display: { md: "none" }, textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 52,
              height: 52,
              borderRadius: 2.5,
              bgcolor: "primary.main",
              color: "#fff",
              mb: 1.5,
            }}
          >
            <ForumOutlined sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Hashlix CRM
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 440,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={3}>
            {/* Heading */}
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Create your account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get started — it's free. No credit card required.
              </Typography>
            </Box>

            <Divider />

            {/* Formik form */}
            <Formik
              initialValues={{ organizationName: "", name: "", email: "", phone: "", password: "" }}
              validationSchema={UserSchema}
              onSubmit={(values, actions) => {
                setTimeout(() => {
                  handleSignUp(values);
                  actions.setSubmitting(false);
                }, 400);
              }}
            >
              <SignUpFields
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                passwordValue={passwordValue}
                setPasswordValue={setPasswordValue}
                strength={strength}
                strengthText={strengthText}
                strengthColor={strengthColor}
              />
            </Formik>

            <Divider />

            <Typography variant="body2" align="center" color="text.secondary">
              {i18n.t("signup.buttons.login")}{" "}
              <Link
                component={RouterLink}
                to="/login"
                color="primary"
                fontWeight={600}
                underline="hover"
              >
                Sign in
              </Link>
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default SignUp;
